import {
  Button,
  Col,
  Divider,
  Form,
  Modal,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import range from "helpers/range";
import { api } from "helpers/api";

import service_date_delays from "helpers/options/service_date_delays";
import { getIssueStatusLabel } from "helpers/options/issue_statuses";
import useUpdate from "hooks/useUpdate";
import InputDate from "components/InputDate";
import { useWorkers } from "context/providers/workers";
import { useCalendarIssues } from "components/CalendarIssues/context";

const disabledDateTime = () => ({
  disabledMinutes: () => [
    ...range(1, 15),
    ...range(16, 30),
    ...range(31, 45),
    ...range(46, 60),
  ],
});

let timeout;

const searchIssues = async (q) => {
  try {
    const { data } = await api.get(
      `/issues?per_page=10&context=list&q[service_date_null]=true&q[identifier_or_client_phone_or_client_first_name_or_client_last_name_or_client_city_or_client_post_code_or_client_street_matches]=%25${q}%25`
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const NewIssue = ({ handleConfirm, refetch, date, close }) => {
  const { workers_list_options } = useWorkers();
  const { worker_id: calendar_worker_id } = useCalendarIssues();
  const [is_searching_issues, setSearchingIssues] = useState(false);
  const [issue_options, setIssueOptions] = useState([]);
  const [issue_id, setIssueId] = useState(null);
  const [worker_id, setWorkerId] = useState(calendar_worker_id);

  const { is_saving, update } = useUpdate({
    path: `/issues/${issue_id}/set_service_date`,
    onSuccess: () => {
      close();
      refetch();
      handleConfirm();
    },
  });

  const handleSubmit = async (values) => {
    const service_date = dayjs(values.service_date).format();

    update({
      worker_id,
      service_date,
      service_date_delay: values.service_date_delay,
    });
  };

  const handleChange = (id) => setIssueId(id || null);

  const handleAsyncSearch = async (q) => {
    try {
      setSearchingIssues(true);
      const data = await searchIssues(q);
      setIssueOptions(
        data.map(({ id, identifier, status }) => ({
          label: (
            <>
              {getIssueStatusLabel(status)} {identifier}
            </>
          ),
          value: id,
        }))
      );
    } catch (error) {
      setIssueOptions([]);
    } finally {
      setSearchingIssues(false);
    }
  };

  const onSearch = useCallback(async (q) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      handleAsyncSearch(q);
    }, 300);
  }, []);

  useEffect(() => {
    onSearch("");
  }, [onSearch]);

  return (
    <Modal
      title="Dodaj zgłoszenie do kalendarza"
      width={700}
      open
      footer={null}
      onCancel={close}
    >
      <Form
        id="day_modal"
        layout="vertical"
        initialValues={{
          service_date: dayjs(date),
          service_date_delay: 0,
          worker_id: null,
        }}
        onFinish={handleSubmit}
      >
        <Space direction="vertical" style={{ marginBottom: 15, width: "100%" }}>
          <Typography.Text>Szukaj zgłoszenia</Typography.Text>
          <Select
            showSearch
            value={issue_id}
            placeholder="Szukaj..."
            style={{
              width: "100%",
            }}
            getPopupContainer={() => document.querySelector("#day_modal")}
            defaultActiveFirstOption={false}
            suffixIcon={is_searching_issues ? "Proszę czekać..." : null}
            filterOption={false}
            onSearch={onSearch}
            onChange={handleChange}
            notFoundContent={null}
            options={issue_options}
          />
        </Space>
        <Row gutter={[12, 12]}>
          <Col xs={24} md={24} lg={12} xl={12}>
            <Form.Item label="Data umówienia" name="service_date" readOnly>
              <InputDate
                format="DD-MM-YYYY HH:mm"
                showTime={{ format: "HH:mm" }}
                disabledTime={disabledDateTime}
                getPopupContainer={() => document.querySelector("#day_modal")}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={24} lg={12} xl={12}>
            <Form.Item
              label="Data umówienia (opóźnienie)"
              name="service_date_delay"
              tooltip="Dostępność klienta w podanym przedziale czasowym"
            >
              <Select
                placeholder="Data umówienia (opóźnienie)"
                options={service_date_delays}
                defaultValue={service_date_delays[0]}
                getPopupContainer={() => document.querySelector("#day_modal")}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[12, 12]}>
          <Col xs={24} md={24} lg={12} xl={12}>
            <Typography className="m-b-5">
              <Typography.Text>Pracownicy</Typography.Text>
            </Typography>
            <Select
              style={{ width: "100%" }}
              value={worker_id}
              getPopupContainer={() => document.querySelector("#day_modal")}
              placeholder="Wybierz pracownika"
              allowClear
              filterOption={false}
              onChange={(id) => setWorkerId(id || null)}
              options={workers_list_options || []}
            />
          </Col>
        </Row>
        <Divider size="medium" />
        <Row justify="end">
          <Space>
            <Button onClick={close}>Anuluj</Button>
            <Button
              type="primary"
              htmlType="submit"
              disabled={!issue_id}
              loading={is_saving}
            >
              Dodaj
            </Button>
          </Space>
        </Row>
      </Form>
    </Modal>
  );
};

export default NewIssue;
